import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import {
  minutesToPosition,
  positionToMinutes,
  formatDuration,
  SLIDER_RANGE,
  SKIP_TO_SERVING_MINUTES,
} from "@brewery/util/durationScale";
import RecipePicker from "./RecipePicker/RecipePicker";
import {
  VALIDATION,
  CARBONATION_DAYS,
} from "@common/constants/recipeConstants";
import PROCESS_TYPES from "@common/constants/processTypes";
import useFermentationOrchestrator from "@brewery/hooks/useFermentationOrchestrator";
import FermentationProgress from "@brewery/components/FermentationProgress/FermentationProgress";
import FermentationWarning from "@brewery/components/FermentationWarning/FermentationWarning";
import "./edit-fermentation-modal.scss";

export function usableTemp(value) {
  const rounded = Math.round(Number(value));
  return Number.isFinite(rounded) && rounded !== 0 ? rounded : null;
}

export function resolveSkipToServingTemp(device) {
  const fromCurrent = usableTemp(device.current_temp);
  const fromTarget =
    fromCurrent !== null ? fromCurrent : usableTemp(device.target_temp);
  const resolved = fromTarget !== null ? fromTarget : 18;
  return Math.min(
    VALIDATION.KEG_FERM_STEP_MAX,
    Math.max(VALIDATION.KEG_FERM_STEP_MIN, resolved),
  );
}

export function EditFermentationModal({ show, handleClose, device, brewerId }) {
  const [temperature, setTemperature] = useState(
    Math.round(device?.target_temp) || 18,
  );
  const [durationMinutes, setDurationMinutes] = useState(10080);
  const [servingTemperature, setServingTemperature] = useState(5);
  const [formMode, setFormMode] = useState("custom");
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [skipPresetApplied, setSkipPresetApplied] = useState(false);

  const {
    phase,
    error,
    inFlight,
    startOrchestration,
    retry,
    resolveConflictAndRetry,
    reset,
  } = useFermentationOrchestrator();

  const sessionId = device?.session_id || device?.active_session;
  const isBusy =
    sessionId != null ||
    (device?.process_type != null &&
      device.process_type !== PROCESS_TYPES.PROC_IDLE);
  const mode = isBusy ? "swap" : "start";

  const settings = useMemo(
    () => ({
      steps: [
        {
          temperature,
          duration_minutes: durationMinutes,
        },
      ],
      serving_temperature: servingTemperature,
    }),
    [temperature, durationMinutes, servingTemperature],
  );

  const skipToServing = useCallback(() => {
    const temp = resolveSkipToServingTemp(device);
    setDurationMinutes(SKIP_TO_SERVING_MINUTES);
    setTemperature(temp);
    setSkipPresetApplied(true);
  }, [device]);

  const apply = useCallback(() => {
    startOrchestration({
      deviceUuid: device.uuid || device.serial_number,
      sessionId,
      mode,
      settings: formMode === "custom" ? settings : undefined,
      beerRecipeId: formMode === "recipe" ? selectedRecipe?.id : undefined,
    });
  }, [
    device,
    sessionId,
    mode,
    formMode,
    settings,
    selectedRecipe,
    startOrchestration,
  ]);

  const onSelectMode = (newMode) => {
    setFormMode(newMode);
    setSelectedRecipe(null);
  };

  const renderSlider = (label, value, min, max, step, onChange, format) => (
    <div className="ferm-slider">
      <label>{label}</label>
      <div className="ferm-slider__value">{format(value)}</div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
      />
    </div>
  );

  const renderModeToggle = () => (
    <div className="ferm-mode-toggle">
      <button
        type="button"
        data-testid="formmode-custom"
        className={formMode === "custom" ? "active" : ""}
        onClick={() => onSelectMode("custom")}
      >
        Custom
      </button>
      <button
        type="button"
        data-testid="formmode-recipe"
        className={formMode === "recipe" ? "active" : ""}
        onClick={() => onSelectMode("recipe")}
      >
        From recipe
      </button>
    </div>
  );

  const renderCustomForm = () => (
    <React.Fragment>
      {renderSlider(
        "Fermentation temperature",
        temperature,
        VALIDATION.KEG_FERM_STEP_MIN,
        VALIDATION.KEG_FERM_STEP_MAX,
        1,
        (e) => {
          setTemperature(parseFloat(e.target.value));
          setSkipPresetApplied(false);
        },
        (v) => `${Math.round(v)} °C`,
      )}
      {renderSlider(
        "Duration",
        minutesToPosition(durationMinutes),
        0,
        SLIDER_RANGE,
        1,
        (e) => {
          setDurationMinutes(positionToMinutes(parseInt(e.target.value, 10)));
          setSkipPresetApplied(false);
        },
        () => formatDuration(durationMinutes),
      )}
      {renderSlider(
        "Serving temperature",
        servingTemperature,
        1,
        15,
        1,
        (e) => setServingTemperature(parseFloat(e.target.value)),
        (v) => `${Math.round(v)} °C`,
      )}
      <button
        type="button"
        className="ferm-skip-to-serving"
        onClick={skipToServing}
      >
        &#9889; Skip to serving
      </button>
      {skipPresetApplied && (
        <div className="ferm-short-note">
          The keg holds at its current temperature for about a minute (the
          hardware minimum &mdash; the firmware counts fermentation in whole
          minutes), so fermentation finishes almost immediately. It then
          carbonates for about {CARBONATION_DAYS} days, which cannot be
          shortened. Along the way the keg will raise prompts for the
          pressurizer, the tap, and a carbonation check &mdash; action each as
          it appears. Those steps are physical, done at the machine, but you can
          confirm them there or from this portal before the keg is ready to
          pour.
        </div>
      )}
      <FermentationWarning mode={mode} />
      <button
        type="button"
        className="btn btn-primary ferm-apply"
        onClick={apply}
      >
        {mode === "swap" ? "Apply changes" : "Start fermentation"}
      </button>
    </React.Fragment>
  );

  const renderForm = () => (
    <React.Fragment>
      {renderModeToggle()}
      {formMode === "custom" ? (
        renderCustomForm()
      ) : (
        <React.Fragment>
          <RecipePicker
            brewerId={brewerId}
            onSelect={(recipe) => setSelectedRecipe(recipe)}
          />
          <FermentationWarning mode={mode} />
          <button
            type="button"
            className="btn btn-primary ferm-apply"
            disabled={!selectedRecipe}
            onClick={apply}
          >
            {mode === "swap" ? "Apply recipe" : "Start fermentation"}
          </button>
        </React.Fragment>
      )}
    </React.Fragment>
  );

  const inProgress = phase !== null;

  const onModalClose = () => {
    if (!inFlight) {
      reset();
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={inFlight ? () => {} : onModalClose}>
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>
          {mode === "swap" ? "Edit fermentation" : "Start fermentation"} &mdash;{" "}
          {device?.title || device?.serial_number}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {inProgress ? (
          <FermentationProgress
            phase={phase}
            error={error}
            inFlight={inFlight}
            retry={retry}
            stopConflictAndRetry={resolveConflictAndRetry}
            handleClose={onModalClose}
          />
        ) : (
          renderForm()
        )}
      </Modal.Body>
    </Modal>
  );
}

EditFermentationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  device: PropTypes.object.isRequired,
  brewerId: PropTypes.number,
};

export default EditFermentationModal;
